/**
 * The page an invite email links to: who invited you, then create the account
 * (email is fixed to the invited address) or continue with Google, or accept
 * straight away when already signed in with that address.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, type AuthOptions } from "../lib/api";

interface Preview {
  email: string;
  role: "member" | "guest";
  workspaceName: string;
  inviterName: string;
  rooms: string[];
  hasAccount: boolean;
  signedInAs: string | null;
}

export function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [opts, setOpts] = useState<AuthOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Preview>(`/api/invites/preview?token=${encodeURIComponent(token)}`).then(setPreview, (err) =>
      setError(err instanceof ApiError ? err.message : "This invite could not be loaded."),
    );
    api<AuthOptions>("/api/auth/options").then(setOpts, () => setOpts({ google: false, firstRun: false }));
  }, [token]);

  async function accept() {
    setBusy(true);
    try {
      await api("/api/invites/accept", { token });
      location.assign("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not accept the invite.");
      setBusy(false);
    }
  }

  async function signUp(e: FormEvent) {
    e.preventDefault();
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/signup", { name, email: preview.email, password, inviteToken: token });
      location.assign("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <section className="card" aria-labelledby="invite-title">
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          Agora
        </div>
        {!preview && !error && <p className="lead">One moment.</p>}
        {error && (
          <>
            <h1 id="invite-title">This invite did not work</h1>
            <div className="notice error" role="alert">
              {error}
            </div>
            <Link className="btn ghost" to="/sign-in">
              Go to sign in
            </Link>
          </>
        )}
        {preview && !error && (
          <>
            <h1 id="invite-title">Join {preview.workspaceName}</h1>
            <p className="lead">
              {preview.inviterName} invited {preview.email}
              {preview.rooms.length > 0 && ` to ${preview.rooms.map((r) => `#${r}`).join(", ")}`}.
              {preview.role === "guest" && " As a Guest you see only the rooms you are invited to."}
            </p>

            {preview.signedInAs === preview.email ? (
              <button className="btn primary" type="button" disabled={busy} onClick={accept}>
                Accept the invite
              </button>
            ) : preview.signedInAs ? (
              <div className="notice info">
                You are signed in as {preview.signedInAs}. Sign out first, then open this link again to accept as {preview.email}.
              </div>
            ) : preview.hasAccount ? (
              <button className="btn primary" type="button" onClick={() => navigate(`/sign-in?invite=${encodeURIComponent(token)}`)}>
                Sign in to accept
              </button>
            ) : (
              <>
                {opts?.google && (
                  <>
                    <a className="btn ghost" href={`/api/auth/google?invite=${encodeURIComponent(token)}`}>
                      Continue with Google
                    </a>
                    <div className="or">or create a password</div>
                  </>
                )}
                <form onSubmit={signUp}>
                  <div className="field">
                    <label htmlFor="inv-email">Email</label>
                    <input id="inv-email" value={preview.email} readOnly />
                  </div>
                  <div className="field">
                    <label htmlFor="inv-name">Name</label>
                    <input id="inv-name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="inv-password">Password</label>
                    <input id="inv-password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                    <small>At least 12 characters. A short sentence works well.</small>
                  </div>
                  <button className="btn primary" type="submit" disabled={busy}>
                    {busy ? "Creating your account" : "Create account and join"}
                  </button>
                </form>
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}
